import { cn } from '@/lib/utils';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Announced to screen readers. Set to null for purely decorative use. */
  label?: string | null;
}

const SIZE_CLASSES = {
  sm: 'size-4 border-2',
  md: 'size-6 border-2',
  lg: 'size-10 border-[3px]',
} as const;

export function Spinner({ size = 'md', className, label = 'Loading' }: SpinnerProps) {
  const isAnnounced = label !== null;

  return (
    // `aria-label` rather than hidden text: `status` is not a name-from-content
    // role, so inner text would not become the region's accessible name.
    <span
      role={isAnnounced ? 'status' : undefined}
      aria-live={isAnnounced ? 'polite' : undefined}
      aria-label={isAnnounced ? label : undefined}
      className={cn('inline-flex items-center gap-2', className)}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block animate-spin rounded-full',
          'border-brand-500/25 border-t-brand-500',
          SIZE_CLASSES[size],
        )}
      />
    </span>
  );
}

export interface SkeletonProps {
  className?: string;
}

/**
 * A shimmering placeholder.
 *
 * Marked `aria-hidden` because a screen reader announcing "loading, loading,
 * loading" for a list of placeholders is noise; the surrounding region's
 * `aria-busy` conveys the state instead.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block rounded-lg',
        'bg-[linear-gradient(90deg,var(--surface-sunken)_25%,var(--surface-raised)_50%,var(--surface-sunken)_75%)]',
        'bg-[length:200%_100%] animate-shimmer',
        className,
      )}
    />
  );
}
